/**
 * checkRequiredFields
 *
 * Pre-flight gate shared by every form action that must not run on an
 * incomplete form (state change, close & create, new version, export, email,
 * workflow-triggered exports). Walks the active form type's default layout,
 * records the required fields the current version leaves empty in
 * `formStore.state.missingRequiredFields`, and returns whether the caller may
 * proceed. `FormRequiredFieldsModal` renders off that same property.
 *
 * Deliberately a plain function reading the live proxy, not a hook: it is a
 * click-time assertion, so no caller may end up subscribed to field values.
 * Threading it through the action hooks as a `useCallback` is what previously
 * re-rendered the whole toolbar on every keystroke.
 *
 * `visibilityMode` comes from the store rather than `useVisibilityMode` because
 * the gate only ever runs from on-screen actions, never from inside the
 * PDF-export portal that overrides the view.
 *
 * The gate applies only to a version the reader can still edit — see
 * `isEditableVersion`.
 */

import {
    UIElement,
    UIField,
    UIEntityReference,
    UIEmbeddedEntity,
    UIElementType,
} from '#types/System.types';
import { formStore, type MissingRequiredField } from '@/store';
import { isDraftFormState } from './isDraftFormState';

/**
 * Emptiness for the required gate only, tested against the persisted value and
 * not against the presence of its `fields` entry. A version is created with
 * `fields: {}` (see `workers/createForm`) and entries appear as fields are
 * written, so an entry proves the field was touched, never that it holds an
 * answer — a field filled and then cleared keeps its entry with an empty value
 * and used to pass the gate.
 *
 * `false` and `0` are real answers and count as filled. `[]` and `{}` count as
 * filled too, so a required multi-value field left empty still passes; that is
 * unchanged behavior, narrow this predicate if it should block.
 */
const isEmptyValue = (value: unknown): boolean =>
    value === null || value === undefined || value === '';

const getElements = (element: UIElement): Array<UIField | UIEntityReference | UIEmbeddedEntity> => {
    if (element.uiElementType === UIElementType.Field
        || element.uiElementType === UIElementType.EntityReference
        || element.uiElementType === UIElementType.EmbeddedEntity) {
        return [element];
    }
    if ('children' in element) {
        return element.children.flatMap(getElements);
    }
    return [];
};

const collectMissing = (): MissingRequiredField[] => {
    const layoutStructure = formStore.state.formType.data?.defaultUILayout?.structure;
    if (!layoutStructure) return [];

    const fields = formStore.state.formVersion.data?.fields;
    const entityReferences = formStore.state.formVersion.data?.entityReferences;
    const visibilityMode = formStore.state.visibilityMode;

    return getElements(layoutStructure)
        .filter((element) => element.required)
        .map((element): MissingRequiredField | null => {
            if (element.uiElementType === UIElementType.Field) {
                return {
                    fieldId: element.fieldId,
                    isEntityReference: false,
                    isVisibleInCurrentMode: visibilityMode === 'internal'
                        || !element.visibilityTypes
                        || element.visibilityTypes.length === 0
                        || element.visibilityTypes.includes(visibilityMode),
                };
            }
            const referenceKey = element.entityGroupId || element.entityId;
            if (!referenceKey) return null;
            return {
                fieldId: referenceKey,
                isEntityReference: true,
                isVisibleInCurrentMode: true,
                entityId: element.entityId,
            };
        })
        .filter((field): field is MissingRequiredField => field !== null)
        .filter((field) => (
            field.isEntityReference
                ? !entityReferences?.[field.fieldId]?.entityRecordId
                : isEmptyValue(fields?.[field.fieldId]?.value)
        ));
};

/**
 * Whether the open version still has writable controls. Only a `draft` version
 * does: every other state disables the whole layout. This is the draft half of
 * the `canWrite` that labels the form Read Only, not the whole of it —
 * `useEvaluateFormAccess` also requires the write grant, and a reader without it
 * is stopped by the callable rather than by this gate.
 *
 * A version with no `state` yet is treated as editable — it is a version the
 * store has not resolved, not a frozen one.
 */
const isEditableVersion = (): boolean => {
    const state = formStore.state.formVersion.data?.state;
    return state === undefined || isDraftFormState(state);
};

/**
 * Reports nothing on a read-only version. There is no control to fill a required
 * field in once the version leaves `draft`, and New Version is the only route to
 * a writable copy of it — so reporting the field blocked every route out at once,
 * including the state change back to `draft` (SYD-2899).
 */
export const checkRequiredFields = (): boolean => {
    const missing = isEditableVersion() ? collectMissing() : [];
    formStore.state.missingRequiredFields = missing;
    return missing.length === 0;
};
